/**
 * The host's send sheet (ADR-037 D8) — opened when an arche calls the
 * `send-to-opener` client-side entity (`useOpener().send`), offering one of
 * its own files to the arche the person came from ("Use in <arche>" in
 * FiasWorks Studio). Every word is the platform's apart from the file's name;
 * the destination is the host's, and only the person's Send moves the file.
 */

import { useEffect, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import type { SendToOpenerResult } from '@fias/client-entities';
import type { ResolvedOpener } from '@/hooks/useArcheOpener';

interface SendToOpenerSheetProps {
  open: boolean;
  /** The arche offering the file. */
  archeId: string;
  archeName: string;
  opener: ResolvedOpener;
  holderId: string | null;
  fileId: string;
  /** The name the arche suggests. */
  name: string | null;
  onResolve: (result: SendToOpenerResult) => void;
}

export function SendToOpenerSheet({
  open,
  archeId,
  archeName,
  opener,
  holderId,
  fileId,
  name,
  onResolve,
}: SendToOpenerSheetProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(name);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The person's own view of the file: its picture and, if the arche named
  // none, its name. Fresh for each sheet; a file they cannot open shows none.
  useEffect(() => {
    let live = true;
    api.getVaultDocumentDownloadUrl(fileId, { inline: true }).then(
      (r) => live && setPreview(r.url),
      () => undefined,
    );
    if (!name) {
      api.getVaultDocument(fileId).then(
        (r) => live && setFileName(r.document.name),
        () => undefined,
      );
    }
    return () => {
      live = false;
    };
  }, [fileId, name]);

  const shown = fileName ?? 'this file';

  async function send() {
    setSending(true);
    setError(null);
    try {
      const landed = await opener.receive({
        holderId,
        sourceArcheId: archeId,
        documentId: fileId,
        name,
      });
      onResolve({ sent: true, name: landed.name });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'It was not sent.');
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !sending && onResolve({ canceled: true })}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Send “{shown}” to {opener.name}?
          </DialogTitle>
          <DialogDescription>
            {archeName} is sending one of its files. It goes into {opener.destination}
            {opener.kind === 'builder'
              ? ', where everyone who opens it can see it.'
              : ', and you can use it there right away.'}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-3">
          {preview && (
            <div className="flex justify-center rounded-md border bg-[repeating-conic-gradient(#e5e7eb_0%_25%,transparent_0%_50%)] bg-[length:12px_12px] p-2">
              <img src={preview} alt="" className="max-h-56 object-contain" />
            </div>
          )}
          {opener.cannotReceive && (
            <p className="text-sm text-muted-foreground">{opener.cannotReceive}</p>
          )}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            disabled={sending}
            onClick={() => onResolve({ canceled: true })}
          >
            Cancel
          </Button>
          <Button onClick={() => void send()} disabled={sending || opener.cannotReceive !== null}>
            {sending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="mr-1 h-4 w-4" aria-hidden="true" />
            )}
            Send to {opener.name}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
